import { useEffect } from "react";
import { X } from "lucide-react";

import DocumentPreviewModal from "../DocumentPreviewModal";
import JobDescriptionView from "../jd/JobDescriptionView";
import type { JDContent } from "../../utils/jobDescription";

export interface JobDescriptionInfo {
  documents: { id: number; title: string; file_type: string; signed: boolean; uploaded_at: string | null }[];
  text: string | null;
  job_title: string | null;
  /** The job description kept in PME People (wins over uploaded files). */
  online?: { id: number; version: number; status: string; content: JDContent; signed_at: string | null; issued_at: string | null } | null;
}

/** The job description beside the self-assessment: the newest JD document if
 *  there is one, else the job description written on the employee record. */
export default function JobDescriptionPanel({ jd, onClose, title = "Your job description" }: { jd: JobDescriptionInfo; onClose: () => void; title?: string }) {
  const online = jd.online ?? null;
  const doc = online ? undefined : jd.documents[0];

  useEffect(() => {
    if (doc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [doc, onClose]);

  if (doc) {
    return <DocumentPreviewModal docked documentId={doc.id} title={`${title}: ${doc.title}`} fileType={doc.file_type} onClose={onClose} />;
  }
  return (
    <div
      className="fixed inset-0 z-[70] flex bg-black/40 p-2 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[min(46vw,760px)] lg:bg-transparent lg:p-0"
      onClick={onClose}
      role="complementary"
      aria-label={title}
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl lg:rounded-none lg:border-l lg:border-gray-200 lg:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
            {title}{jd.job_title ? ` · ${jd.job_title}` : ""}
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {online ? (
            <>
              <p className="mb-3 text-xs text-gray-500">
                Version {online.version}
                {online.signed_at ? ", signed" : online.status === "issued" ? ", waiting for signature" : ""}
              </p>
              <JobDescriptionView content={online.content} />
            </>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{jd.text}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** True when there is something to show. */
export function hasJobDescription(jd: JobDescriptionInfo | null): jd is JobDescriptionInfo {
  return !!jd && (!!jd.online || jd.documents.length > 0 || !!(jd.text && jd.text.trim()));
}
